import { ComponentChildren } from 'preact';
import type {
  ItemInfo,
  PictoChartItemData,
  MarkerRendererContext,
  PictoRendererOptions,
  DatatipContext
} from './pictoChart.type';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { BundleType } from '../resources/nls/bundle';
import { MarkerShapes } from '../utils/UNSAFE_visTypes/common';
import { colorSchemeVars } from '../Common/themes/themeContract.css';
import { rgb, rgba } from '../UNSAFE_Theme';
import { SvgSymbol } from '../PRIVATE_SvgShapes';
import { usePictoChartContext } from './PictoChartContext';
import { Property } from 'csstype';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { generateAriaLabel } from '#utils/UNSAFE_visUtils/accUtils';

/**
 * Checks if the item is highlighted.
 * @param id Id of the item
 * @param highlighted The set of highlighted item
 */
export function isItemHighlighted<K>(id: K, highlightedIds?: K[]) {
  if (!highlightedIds || highlightedIds.length === 0) {
    return true;
  }
  return new Set(highlightedIds).has(id);
}

/**
 * Checks if the item is highlighted.
 * @param id Id of the item
 * @param selectedIds The set of highlighted item
 */
export function isItemSelected<K>(id: K, selectedIds?: K[]) {
  if (!selectedIds || selectedIds.length === 0) {
    return false;
  }
  return new Set(selectedIds).has(id);
}

/**
 * Returns the value of the data-oj-item-index attribute for a given element.
 * @param element The HTML element.
 */
export function getItemInfo(element: HTMLElement) {
  const itemIdx = element.dataset['idx'];
  if (itemIdx === undefined) return;

  return {
    idx: Number(itemIdx)
  };
}

/**
 * Returns the item props for PictoChart.
 * @param item The picto chart item.
 * @param itemIdx The item index of the item.
 * @param countIdx The item count of the item.
 * @param isReadOnly Whether the picto chart is readonly.
 * @param hoveredItem The info of the currently hovered item.
 * @param selectedIds The selected Ids
 * @param focusedItem The info of currently focused item.
 * @param highlightedIds The highlighted ids.
 * @param activeId The active Id
 */
export function getItemProps<K, D extends PictoChartItemData<K>>(
  item: D,
  itemIdx: number,
  countIdx: number,
  isReadOnly: boolean,
  hoveredItem?: ItemInfo,
  selectedIds?: Array<K>,
  focusedItem?: ItemInfo,
  highlightedIds?: Array<K>,
  activeId?: string
) {
  let isCurrent = false;
  if (!isReadOnly) {
    const isFocusedItem = !!focusedItem && itemIdx === focusedItem.idx;
    const isHoveredItem = !!hoveredItem && itemIdx === hoveredItem.idx;
    isCurrent = !!(
      (isFocusedItem && focusedItem?.isCurrent) ||
      (isHoveredItem && hoveredItem?.isCurrent)
    );
  }
  const isHighlighted = isItemHighlighted<K>(item.id, highlightedIds);
  const isSelected = isItemSelected(item.id, selectedIds);
  const hasSelection = selectedIds && selectedIds.length !== 0;

  return {
    isCurrent,
    isHighlighted: isHighlighted,
    isSelected: isSelected,
    hasSelection,
    isFocused: focusedItem ? hasFocusRing(focusedItem, itemIdx) : false,
    key: item.id,
    itemIdx,
    countIdx,
    activeId: isCurrent ? activeId : undefined,
    isReadOnly,
    item: item
  };
}

/**
 * Compares if two ItemInfo are equal
 * @param item1
 * @param item2
 * @returns
 */
export function isEqualItem(item1?: ItemInfo, item2?: ItemInfo) {
  return item1?.idx === item2?.idx;
}

/**
 * Checks if the item is the currentItem and if the focus ring
 * is visible.
 * @param itemInfo ItemInfo
 * @param itemIdx Item index
 */
export function hasFocusRing(itemInfo: ItemInfo, itemIdx: number) {
  if (!itemInfo.isFocusVisible) return false;
  return itemInfo.idx === itemIdx;
}

/**
 *
 */
export function getCountWithTotal(itemCount: number, totalCount: number, translations: BundleType) {
  const totalWithCountLabel = `${translations?.vis_labelCountWithTotal({
    itemCount: itemCount.toString(),
    totalCount: totalCount.toString()
  })}`;
  return totalWithCountLabel;
}

/**
 * Returns the aria properties for the picto chart.
 * @param isReadOnly { boolean } If the picto chart is readonly.
 * @param accessibleLabel { string } The aria-label set by the app.
 * @returns The aria props.
 */
export function getItemAriaProps(
  itemCount: number,
  totalCount: number,
  supportsSelection: boolean,
  label?: string,
  translations?: BundleType,
  isSelected?: boolean,
  hasSelection?: boolean,
  accessibleLabel?: string,
  activeId?: string
): {
  'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
  role: HTMLAttributesSignalExcluded['role'];
  id?: string;
} {
  const totalWithCountLabel = translations
    ? getCountWithTotal(itemCount, totalCount, translations)
    : '';

  accessibleLabel = accessibleLabel || `${label ? label + ': ' : ''}${totalWithCountLabel}`; // TODO: use a translation string for :

  return {
    'aria-label':
      generateAriaLabel(translations!, accessibleLabel, {
        isSelected: !supportsSelection && !hasSelection ? undefined : isSelected
      }) || undefined,
    role: 'img',
    id: activeId
  };
}

/**
 * Returns the marker props for SVGSymbol
 */
export function getMarkerProps(
  shape: MarkerShapes,
  color?: ColorProps['color'],
  isFocused?: boolean,
  isSelected?: boolean,
  isCurrent?: boolean,
  supportsSelection?: boolean
) {
  const isHuman = shape === 'human';
  const applyHover = isFocused || (isCurrent && supportsSelection);
  const isNoneEqualSides = shape === 'ellipse' || shape === 'rectangle';
  color = color !== '' ? color : rgba(colorSchemeVars.palette.neutral['190'], 0.15);
  const outerBorderColor = isSelected ? colorSchemeVars.textIcon.primary : applyHover ? color : '';

  return {
    markerShape: shape,
    tx: isHuman ? 70 : undefined,
    ty: isHuman ? 60 : undefined,
    markerColor: color,
    borderColor: isSelected || applyHover ? rgb(colorSchemeVars.palette.neutral[0]) : color,
    outerBorderColor,
    markerScale: isNoneEqualSides && !outerBorderColor ? 1 : 0.85,
    setAspectRatioNone: isNoneEqualSides ? true : false
  };
}

// Creating component to read the context
const PictoRenderer = <K,>({
  context,
  markerProps
}: {
  context: MarkerRendererContext<K>;
  markerProps: PictoRendererOptions;
}) => {
  const { supportsSelection } = usePictoChartContext();
  const markerProp = getMarkerProps(
    markerProps.shape,
    markerProps.color,
    context.state.focused,
    context.state.selected,
    context.state.hovered,
    supportsSelection
  );

  return <SvgSymbol {...markerProp} />;
};

/**
 * Returns the marker renderer for SVGSymbol
 */
export function getPictoDefaultRenderers<
  K,
  D extends PictoChartItemData<K> = PictoChartItemData<K>
>(rendererOptions: (data: D) => PictoRendererOptions) {
  return {
    markerRenderer: (context: MarkerRendererContext<D>) => {
      const props = rendererOptions(context.data); // TODO: should we add default values
      return <PictoRenderer context={context} markerProps={props} />;
    },
    datatipRenderer: (context: DatatipContext<D>) => {
      const props = rendererOptions(context.data);
      return { borderColor: props.color };
    }
  };
}

export function getTotalCount<K>(items: PictoChartItemData<K>[]) {
  let totalCount = 0;
  for (let i = 0; i < items.length; i++) {
    totalCount += items[i].count || 1;
  }
  return totalCount;
}

/**
 * Returns the text for the picto chart datatip.
 * @param items The picto chart items data.
 * @param focusedItem The item currently keyboard focused.
 * @param hoveredItem The item that is currently hovered.
 * @param datatip The datatip.
 */
export function getDatatipContent<K, D extends PictoChartItemData<K>>(
  items: D[],
  getItem: (itemInfo: ItemInfo, items: D[]) => D | undefined,
  totalCount: number,
  focusedItem: ItemInfo,
  hoveredItem?: ItemInfo,
  datatip?: (context: DatatipContext<D>) => {
    content?: ComponentChildren;
    borderColor?: Property.BorderColor;
  },
  translations?: BundleType
) {
  let itemInfo;
  const isPointerActive = hoveredItem?.isCurrent;
  if (isPointerActive && items[hoveredItem.idx].id != null) {
    itemInfo = hoveredItem;
  }
  if (focusedItem && focusedItem.isCurrent) {
    itemInfo = focusedItem;
  }
  let _content, countWithTotal;
  let _borderColor: string = rgba(colorSchemeVars.palette.neutral['190'], 0.15);
  const item = itemInfo && getItem(itemInfo, items);
  if (item && datatip) {
    // custom datatip
    const datatipContext = item && { data: item };
    const { content, borderColor } = datatip(datatipContext);
    _content = content;
    _borderColor = borderColor || _borderColor;
    countWithTotal = translations
      ? getCountWithTotal(item.count || 1, totalCount, translations)
      : '';
  }
  return {
    borderColor: _borderColor,
    content:
      _content ||
      (itemInfo && getItem(itemInfo, items)?.accessibleLabel) ||
      (itemInfo && getItem(itemInfo, items)?.label + ': ' + countWithTotal) ||
      countWithTotal
  };
}
